import {Button} from "@zendeskgarden/react-buttons";
import {Dropdown, Field, Item, Label, Menu, Select} from "@zendeskgarden/react-dropdowns";
import {Field as FormField, Input as FormInput, Label as FormLabel} from "@zendeskgarden/react-forms";
import cn from "classnames";
import {memo, ReactElement, useEffect, useMemo, useRef, useState} from "react";
import {toast} from "react-toastify";
import {
    AllPropertyForecastLocksQuery,
    CustomDriverAssumptionsUpdateInput,
    useDeleteCustomDriversMutation,
    useLoadCustomDriversQuery,
    useSetCustomDriversStatusMutation,
    useUpdateCustomDriverAssumptionsMutation,
    VersionType
} from "../../../__generated__/generated_types";
import {CustomBulkDriverNoteModal} from "../../../components/driver-note/BulkDriverNoteModal";
import {CustomDriverNoteModal} from "../../../components/driver-note/DriverNoteModal";
import VZSaveStatus from "../../../components/vz-ui/vz-save-status/VZSaveStatus";
import {useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {useProperties} from "../../../contexts/properties/PropertiesContext";
import {round, roundArray} from "../../../utils/math";
import {getMonths} from "../../../utils/months-helper";
import {CopyCustomDriverAssumptionsModal, CopyCustomDriverSourceDriver} from "./CopyCustomDriverAssumptionsModal";
import {CreateCustomDriverTooltipModal} from "./CreateCustomDriverTooltipModal";
import {DeleteLineItemsModal} from "./DeleteLineItemsModal";
import {SetStatusModal} from "./SetStatusModal";
import {
    CompoundSimpleHotRow,
    SimpleHoTCellStyle,
    SimpleHoTChange,
    SimpleHoTRow,
    SimpleHoTWrapper
} from "./SimpleHoTWrapper";
import {SimpleHoTWrapperControls} from "./SimpleHoTWrapperControls";
import {addToArray, cellTypeToNumber, divideArray, filterSelectedLineItems, toLockLookup, getStatusText, GroupedCustomDriver} from "./logic";
import * as css from "./styles/CustomDriverTab.module.scss";
import * as controls from "./styles/SimpleHoTWrapper.module.scss";

export interface CustomDriverTabProps {
    budgetYear: number;
    versionType: VersionType.Budget | VersionType.Reforecast;
    locks: AllPropertyForecastLocksQuery | undefined;
    locksLoading: boolean;
}

const COL_TO_MONTH: Record<string, number> = {
    "values.0": 0,
    "values.1": 1,
    "values.2": 2,
    "values.3": 3,
    "values.4": 4,
    "values.5": 5,
    "values.6": 6,
    "values.7": 7,
    "values.8": 8,
    "values.9": 9,
    "values.10": 10,
    "values.11": 11
};

enum GroupByOptions {
    CustomItem,
    Property,
    Account
}

const GroupByLabels = {
    [GroupByOptions.CustomItem]: "Custom Item",
    [GroupByOptions.Property]: "Property",
    [GroupByOptions.Account]: "Account"
};

export function CustomDriverTab(props: CustomDriverTabProps): ReactElement {
    const {data, loading, refetch} = useLoadCustomDriversQuery({
        fetchPolicy: "no-cache",
        variables: {
            budgetYear: props.budgetYear,
            versionType: props.versionType
        },
    });
    const [updateCustomDriver, {loading: updateCustomDriverLoading}] = useUpdateCustomDriverAssumptionsMutation({
        notifyOnNetworkStatusChange: true
    });
    const [setCustomDriversStatus, {loading: setStatusLoading}] = useSetCustomDriversStatusMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [deleteCustomDrivers, {loading: deleteCustomDriversLoading}] = useDeleteCustomDriversMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [collectedChanges, setCollectedChanges] = useState<SimpleHoTChange[]>([]);

    const addButtonRef = useRef<HTMLButtonElement>(null);
    const [addTooltipVisible, setAddTooltipVisible] = useState<boolean>(false);
    const [setStatusModal, setSetStatusModal] = useState<{
        driverIds: string[];
        itemName: string[];
        initialLocked: boolean;
        initialValuesLocked: boolean;
    } | null>(null);
    const [deleteModal, setDeleteModal] = useState<{driverIds: string[]} | null>(null);
    const [copyModal, setCopyModal] = useState<{
        sourceDriver: CopyCustomDriverSourceDriver;
        destinationProperties: {id: string; name: string, disabled?: boolean}[];
        defaultSelectedProperties: string[],
        accounts: {id: string; label: string}[];
    } | null>(null);

    const {properties} = useProperties();
    const {accounts} = useChartOfAccounts();

    const [lastSaved, setLastSaved] = useState<Date>(new Date());
    const [groupBy, setGroupBy] = useState<GroupByOptions>(GroupByOptions.CustomItem);
    const [groupByFilter, setGroupByFilter] = useState<string | undefined>(undefined);
    const [tmpGroupByFilter, setTmpGroupByFilter] = useState<string | undefined>(undefined);

    const [editingNoteLineId, setEditingNoteLineId] = useState<string | null>(null);
    const [editingNoteLineName, setEditingNoteLineName] = useState<string | null>(null);
    const [bulkEditingNoteLineIds, setBulkEditingNoteLineIds] = useState<string[] | null>(null);
    const [bulkEditingNoteLineNames, setBulkEditingNoteLineNames] = useState<string[] | null>(null);

    useEffect(() => {
        const to = setTimeout(() => {
            setGroupByFilter(tmpGroupByFilter);
        }, 500);
        return () => clearTimeout(to);
    }, [tmpGroupByFilter]);

    const rows = useMemo<GroupedCustomDriver[]>(() => {
        if (loading || !data?.loadCustomDrivers || props.locksLoading) {
            return [];
        }

        const accountsMap = accounts.toIdMap(a => a.id);
        const propertyMap = properties?.toIdMap(p => p.id) ?? {};

        return data.loadCustomDrivers.map(d => {
            const property = propertyMap[d.propertyId];
            const account = accountsMap[d.destinationAccountId];

            return {
                driverId: d.id,
                propertyId: d.propertyId,
                destinationAccountId: d.destinationAccountId,
                itemName: d.itemName,
                locked: d.locked,
                valuesLocked: d.valuesLocked,
                amounts: d.amounts,
                percents: d.percents,
                counts: d.counts,
                propertyName: property?.name ?? d.propertyId,
                accountLabel: account ? `${account.accountNumber} - ${account.name}` : d.destinationAccountId,
            };
        });
    }, [data, loading, properties, accounts, props.locksLoading]);

    const locksLookup = useMemo<Set<string>>(() => {
        return toLockLookup(props.versionType, props.locks);
    }, [props.versionType, props.locks]);

    const groupedRows = useMemo(() => {
        let sortedRows = rows;

        if(groupBy === GroupByOptions.CustomItem) {
            sortedRows = rows.sortBy("itemName", "propertyName", "accountLabel");
        } else if(groupBy === GroupByOptions.Property) {
            sortedRows = rows.sortBy("propertyName", "itemName", "accountLabel");
        } else {
            sortedRows = rows.sortBy("accountLabel", "itemName", "propertyName");
        }

        const grouped = sortedRows.groupBy(d => {
            if(groupBy === GroupByOptions.CustomItem) {
                return d.itemName;
            } else if(groupBy === GroupByOptions.Property) {
                return d.propertyName;
            } else {
                return d.accountLabel;
            }
        });

        if(groupByFilter) {
            const filtered: typeof grouped = {};
            for(const [key, val] of Object.entries(grouped)) {
                if(key.toLowerCase().includes(groupByFilter.toLowerCase())) {
                    filtered[key] = val;
                }
            }
            return filtered;
        }
        return grouped;
    }, [rows, groupBy, groupByFilter]);

    const [simpleHotRows, setSimpleHotRows] = useState<SimpleHoTRow[]>([]);

    useEffect(() => {
        const keys = Object.keys(groupedRows).sort();
        const hotRows: CompoundSimpleHotRow[] = [];
        for(const key of keys) {
            const val = groupedRows[key];
            const amountsTotal: number[] = new Array(12).fill(0);
            if(val) {
                const childRows: CompoundSimpleHotRow[] = [];
                let childCount = 0 ;
                let lockedCount = 0;
                let valuesLockedCount = 0;
                for(const child of val) {
                    const childAmounts = child.amounts.map(v => v ?? 0);
                    const childPercents = child.percents.map(v => v ?? 0);
                    const childCounts = child.counts.map(v => v ?? 0);
                    addToArray(amountsTotal, childAmounts);
                    const status = [
                        child.locked ? "Edit-Only" : undefined,
                        child.valuesLocked ? "Read-Only" : undefined
                    ].filter(Boolean).join(", ");
                    if(child.locked) {
                        lockedCount++;
                    }
                    if(child.valuesLocked) {
                        valuesLockedCount++;
                    }
                    childRows.push({
                        isCompound: true,
                        values: [
                            {
                                label: key,
                                property: child.propertyName,
                                account: child.accountLabel,
                                customItem: child.itemName,
                                status: status,
                                values: childAmounts,
                                type: "$ Amounts",
                                valuesType: "amounts"
                            },
                            {
                                label: key,
                                property: child.propertyName,
                                account: child.accountLabel,
                                customItem: child.itemName,
                                status: status,
                                values: childPercents,
                                type: `% of ${child.itemName}`,
                                valuesType: "percents"
                            },
                            {
                                label: key,
                                property: child.propertyName,
                                account: child.accountLabel,
                                customItem: child.itemName,
                                status: status,
                                values: childCounts,
                                type: `# of ${child.itemName}`,
                                valuesType: "counts"
                            }
                        ],
                        verticalColumnMerges: [0, 1, 2, 3],
                        extraData: {
                            customItem: child,
                            type: "amounts"
                        }
                    });
                    childCount++;
                }

                let status: string | string[] = [];

                if (lockedCount > 0) {
                    status.push(lockedCount === val.length ? "Edit-Only" : "Partially Edit-Only");
                }

                if (valuesLockedCount > 0) {
                    status.push(valuesLockedCount === val.length ? "Read-Only" : "Partially Read-Only");
                }

                status = status.join(", ");

                hotRows.push({
                    isCompound: true,
                    verticalColumnMerges: [],
                    values: [{
                        label: key,
                        property: null,
                        account: null,
                        customItem: null,
                        status: status,
                        type: "Avg $ Amount",
                        values: roundArray(divideArray(amountsTotal, childCount), 2)
                    }],
                    children: childRows,
                    summaryUpdate: change => {
                        if(change.compoundRowValuesIndex !== 0) {
                            return undefined;
                        }

                        const colIdx = COL_TO_MONTH[change.col];
                        if(colIdx === undefined) {
                            return undefined;
                        }

                        const child = change.parent as CompoundSimpleHotRow;
                        const childValues = (child.values[0] as {values: number[]}).values;
                        if(colIdx < childValues.length) {
                            const newVal = cellTypeToNumber(change.newValue);
                            if(newVal !== null) {
                                childValues[colIdx] = newVal;
                            }
                        }

                        const row = change.parent as CompoundSimpleHotRow;
                        if(row.children && row.children.length > 0) {
                            let total = 0;
                            for(const child of row.children) {
                                const childValues = (child.values[0] as { values: number[] }).values;
                                const val = childValues[colIdx];
                                if(val !== undefined) {
                                    const local = cellTypeToNumber(val);
                                    if(local !== null) {
                                        total += local;
                                    }
                                }
                            }
                            return round(total / row.children.length, 2);
                        }
                        return undefined;
                    }
                });
            }
        }
        setSimpleHotRows(hotRows);
    }, [groupedRows]);

    useEffect(() => {
        if(updateCustomDriverLoading || collectedChanges.length === 0) {
            return;
        }

        const byId: Record<string, CustomDriverAssumptionsUpdateInput> = {};
        for(const change of collectedChanges) {
            if(!change.row.isCompound || change.compoundRowValuesIndex === undefined) {
                continue;
            }
            const values = change.row.values[change.compoundRowValuesIndex];
            if(!values) {
                continue;
            }

            const customItem = change.row.extraData?.["customItem"] as GroupedCustomDriver;
            const type = values["valuesType"] as "amounts" | "percents" | "counts" | undefined;
            if(type && customItem && typeof change.col === "string") {
                const driverId = customItem.driverId;
                let update = byId[driverId];
                const month = COL_TO_MONTH[change.col];
                if(month !== undefined) {
                    if(!update) {
                        update = {
                            id: driverId
                        };
                        byId[driverId] = update;
                    }

                    const val= typeof change.newValue === "number" ? change.newValue?.toString() ?? null : change.newValue;
                    if(type === "percents") {
                        switch(month) {
                            case 0: update.percentMonth0 = val; break;
                            case 1: update.percentMonth1 = val; break;
                            case 2: update.percentMonth2 = val; break;
                            case 3: update.percentMonth3 = val; break;
                            case 4: update.percentMonth4 = val; break;
                            case 5: update.percentMonth5 = val; break;
                            case 6: update.percentMonth6 = val; break;
                            case 7: update.percentMonth7 = val; break;
                            case 8: update.percentMonth8 = val; break;
                            case 9: update.percentMonth9 = val; break;
                            case 10: update.percentMonth10 = val; break;
                            case 11: update.percentMonth11 = val; break;
                        }
                    } else if (type === "amounts") {
                        switch(month) {
                            case 0: update.amountMonth0 = val; break;
                            case 1: update.amountMonth1 = val; break;
                            case 2: update.amountMonth2 = val; break;
                            case 3: update.amountMonth3 = val; break;
                            case 4: update.amountMonth4 = val; break;
                            case 5: update.amountMonth5 = val; break;
                            case 6: update.amountMonth6 = val; break;
                            case 7: update.amountMonth7 = val; break;
                            case 8: update.amountMonth8 = val; break;
                            case 9: update.amountMonth9 = val; break;
                            case 10: update.amountMonth10 = val; break;
                            case 11: update.amountMonth11 = val; break;
                        }
                    } else if (type === "counts") {
                        switch(month) {
                            case 0: update.countMonth0 = val; break;
                            case 1: update.countMonth1 = val; break;
                            case 2: update.countMonth2 = val; break;
                            case 3: update.countMonth3 = val; break;
                            case 4: update.countMonth4 = val; break;
                            case 5: update.countMonth5 = val; break;
                            case 6: update.countMonth6 = val; break;
                            case 7: update.countMonth7 = val; break;
                            case 8: update.countMonth8 = val; break;
                            case 9: update.countMonth9 = val; break;
                            case 10: update.countMonth10 = val; break;
                            case 11: update.countMonth11 = val; break;
                        }
                    }
                }
            }
        }

        setCollectedChanges([]);

        updateCustomDriver({
            variables: {
                items: Object.values(byId)
            }
        }).then(res => {
            if(res.data?.updateCustomDriverAssumptions) {
                setLastSaved(new Date());
                return;
            }
            toast.error("Failed to save changes");
        }).catch(() => {
            toast.error("Failed to save changes");
        });
    }, [updateCustomDriverLoading, collectedChanges]);

    function updateGroupedRowData(change: SimpleHoTChange): void {
        let newVal = typeof change.newValue === "string" ? parseFloat(change.newValue) : change.newValue;
        if(Number.isNaN(newVal)) {
            newVal = null;
        }

        if(change.row.isCompound && change.compoundRowValuesIndex !== undefined) {
            const driver = change.row.extraData?.["customItem"] as GroupedCustomDriver;
            const type = change.row.values[change.compoundRowValuesIndex]?.["valuesType"] as "amounts" | "percents" | "counts" | undefined;
            const index = COL_TO_MONTH[change.col];
            if(type !== undefined && index !== undefined) {
                if(type === "amounts") {
                    driver.amounts[index] = newVal;
                } else if(type === "percents") {
                    driver.percents[index] = newVal;
                } else if(type === "counts") {
                    driver.counts[index] = newVal;
                }
            }
        }
    }

    function handleChange(changes: SimpleHoTChange[]): void {
        for(const change of changes) {
            updateGroupedRowData(change);
        }
        setCollectedChanges([...collectedChanges, ...changes]);
    }

    function getDataProperty(col: number, groupBy: GroupByOptions): string {
        if(groupBy === GroupByOptions.CustomItem) {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "property";
            } else {
                return "account";
            }
        } else if(groupBy === GroupByOptions.Property) {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "customItem";
            } else {
                return "account";
            }
        } else {
            if(col === 0) {
                return "label";
            } else if(col === 1) {
                return "customItem";
            } else {
                return "property";
            }
        }
    }

    function getColumnLabel(col: number, groupBy: GroupByOptions): string {
        if(groupBy === GroupByOptions.CustomItem) {
            if(col === 0) {
                return "Custom Item";
            } else if(col === 1) {
                return "Property";
            } else {
                return "Account";
            }
        } else if(groupBy === GroupByOptions.Property) {
            if(col === 0) {
                return "Property";
            } else if(col === 1) {
                return "Custom Item";
            } else {
                return "Account";
            }
        } else {
            if(col === 0) {
                return "Account";
            } else if(col === 1) {
                return "Custom Item";
            } else {
                return "Property";
            }
        }
    }

    const months = getMonths(properties?.[0] ?? undefined);
    return (
        <div className={css.customDriverTabWrapper}>
            <SimpleHoTWrapperControls>
                <div className={cn(controls.simpleHoTWrapperControls, css.customDriverTabControls)}>
                    <div className={css.filters}>
                        <Dropdown
                                selectedItem={groupBy}
                                onSelect={item => setGroupBy(item as GroupByOptions)}
                        >
                            <Field>
                                <Label>Primary Column</Label>
                                <Select>{GroupByLabels[groupBy]}</Select>
                            </Field>
                            <Menu>
                                <Item value={GroupByOptions.CustomItem}>{GroupByLabels[GroupByOptions.CustomItem]}</Item>
                                <Item value={GroupByOptions.Property}>{GroupByLabels[GroupByOptions.Property]}</Item>
                                <Item value={GroupByOptions.Account}>{GroupByLabels[GroupByOptions.Account]}</Item>
                            </Menu>
                        </Dropdown>
                        <FormField>
                            <FormLabel>Filter Primary Column</FormLabel>
                            <FormInput
                                    value={tmpGroupByFilter ?? ""}
                                    onChange={e => setTmpGroupByFilter(e.target.value ?? undefined)}
                            />
                        </FormField>
                    </div>
                    <div className={css.actions}>
                        <div>
                            <Button size="small" ref={addButtonRef} onClick={() => setAddTooltipVisible(!addTooltipVisible)}>
                                Add Custom Driver
                            </Button>
                        </div>
                        <div className={css.saveStatus}>
                            <VZSaveStatus isSaving={collectedChanges.length > 0 || updateCustomDriverLoading} lastSaved={lastSaved} />
                        </div>
                    </div>
                </div>
            </SimpleHoTWrapperControls>
            <MemoizedSimpleHoTWrapper
                withControls
                dataLoading={loading || props.locksLoading}
                rows={simpleHotRows}
                nestedRows={true}
                columns={[
                    {data: getDataProperty(0, groupBy)},
                    {data: getDataProperty(1, groupBy)},
                    {data: getDataProperty(2, groupBy)},
                    {data: "status"},
                    {data: "type"},
                    {data: "values.0", type: "numeric", allowInvalid: false},
                    {data: "values.1", type: "numeric", allowInvalid: false},
                    {data: "values.2", type: "numeric", allowInvalid: false},
                    {data: "values.3", type: "numeric", allowInvalid: false},
                    {data: "values.4", type: "numeric", allowInvalid: false},
                    {data: "values.5", type: "numeric", allowInvalid: false},
                    {data: "values.6", type: "numeric", allowInvalid: false},
                    {data: "values.7", type: "numeric", allowInvalid: false},
                    {data: "values.8", type: "numeric", allowInvalid: false},
                    {data: "values.9", type: "numeric", allowInvalid: false},
                    {data: "values.10", type: "numeric", allowInvalid: false},
                    {data: "values.11", type: "numeric", allowInvalid: false},
                ]}
                cellStyler={(_row, col, rowData, _cellValue, valuesIndex?: number) => {
                    const style: SimpleHoTCellStyle = {};
                    const isParentRow = rowData.children !== undefined;
                    if([0, 1, 2, 3, 4].includes(col) || isParentRow) {
                        style.isReadOnly = true;
                    }
                    const customItem = rowData?.extraData?.["customItem"] as GroupedCustomDriver;
                    if(customItem) {
                        if(locksLookup.has(customItem.propertyId)) {
                            style.isReadOnly = true;

                            if(col === 0) {
                                style.cssClasses = ["lockedColumn"];
                            }
                        }

                        if(valuesIndex !== undefined && rowData.isCompound && col >= 5) {
                            const row = rowData as CompoundSimpleHotRow;
                            const type = row.values[valuesIndex]?.["valuesType"] as "amounts" | "percents" | "counts" | undefined;
                            if(type === "percents") {
                                style.numericFormat = "0,0.00";
                                style.cssClasses = style.cssClasses ===  undefined ? [] : [...style.cssClasses];
                                style.cssClasses.push("percentage-cell");
                            } else if(type === "amounts") {
                                style.numericFormat = "$0,0.00";
                            } else if(type === "counts") {
                                style.numericFormat = "0,0.00";
                            }
                        }
                    } else if(col >= 5) {
                        const childrenLen = rowData.children?.length ?? 0;
                        if(childrenLen > 0) {
                            style.numericFormat = "$0,0.00";
                        }
                    }
                    return style;
                }}
                contextMenu={{
                    "copyToOtherProperties": {
                        name: "Copy To Other Properties/Accounts",
                        hidden: (selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            if (!(selectedRows.size === 1 && selectedCols.size === 1 && isValidCol)) {
                                // Copy is possible when right click on non-assumption data column and single row selected
                                return true;
                            }
                            return false;
                        },
                        disabled: (_selectedRows, _selectedCols, selectedRowData) => {
                            if (selectedRowData.length !== 1) {
                                return true;
                            }
                            const row = selectedRowData[0]?.rowData;
                            const customItem = row?.extraData?.["customItem"] as GroupedCustomDriver | undefined;
                            if (!customItem) {
                                return true;
                            }
                            return false;
                        },
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            if (selectedRowData.length !== 1) {
                                return;
                            }
                            const row = selectedRowData[0]?.rowData;
                            const customItem = row?.extraData?.["customItem"] as GroupedCustomDriver | undefined;
                            if (!customItem) {
                                return;
                            }

                            const matchingUnlockedDriverPropertyIds = rows.filter(driver =>
                                driver.itemName === customItem.itemName
                                && !locksLookup.has(driver.propertyId)
                            ).map(p => p.propertyId).dedupe();

                            const propertySelectOptions = (properties ?? []).map(p => ({
                                id: p.id,
                                name: p.name,
                                disabled: locksLookup.has(p.id),
                            }));

                            if (propertySelectOptions.every(p => p.disabled === true)) {
                                toast.error("All properties locked for this period");
                                return;
                            }

                            setCopyModal({
                                sourceDriver: {
                                    driverId: customItem.driverId,
                                    name: customItem.itemName,
                                    propertyId: customItem.propertyId,
                                    propertyName: customItem.propertyName,
                                    destinationAccountId: customItem.destinationAccountId,
                                    accountLabel: customItem.accountLabel,
                                    status: getStatusText(customItem),
                                    locked: customItem.locked,
                                    valuesLocked: customItem.valuesLocked,
                                    amounts: customItem.amounts,
                                    percents: customItem.percents,
                                    counts: customItem.counts,
                                },
                                destinationProperties: propertySelectOptions,
                                defaultSelectedProperties: matchingUnlockedDriverPropertyIds,
                                accounts: accounts.map(a => ({id: a.id, label: `${a.accountNumber} ${a.name}`})),
                            });
                        }
                    },
                    "delete": {
                        name: "Delete Custom Drivers",
                        hidden: (_selectedRows, selectedCols) => {
                            return !(selectedCols.size === 1 && selectedCols.has(0));
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const selectedDriverIds = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for (const selectedItem of filteredSelection) {
                                const rowData = selectedItem.rowData;
                                if (rowData.children && rowData.children.length > 0) {
                                    for (const child of rowData.children) {
                                        const customItem = child.extraData?.["customItem"] as GroupedCustomDriver;
                                        if (customItem && !selectedDriverIds.has(customItem.driverId)) {
                                            selectedDriverIds.add(customItem.driverId);
                                        }
                                    }
                                } else {
                                    const customItem = rowData.extraData?.["customItem"] as GroupedCustomDriver;
                                    if (customItem && !selectedDriverIds.has(customItem.driverId)) {
                                        selectedDriverIds.add(customItem.driverId);
                                    }
                                }
                            }

                            setDeleteModal({
                                driverIds: Array.from(selectedDriverIds)
                            });
                        }
                    },
                    "changeStatus": {
                        name: "Set Non-Admin Access",
                        hidden: (_selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const itemIds: string[] = [];
                            const drivers: GroupedCustomDriver[] = [];
                            const itemNames = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for(const selectedItem of filteredSelection) {
                                const row = selectedItem.rowData;
                                if(row.children && row.children.length > 0) {
                                    for(const child of row.children) {
                                        const customItem = child.extraData?.["customItem"] as GroupedCustomDriver;
                                        if(customItem) {
                                            itemIds.push(customItem.driverId);
                                            drivers.push(customItem);
                                            itemNames.add(customItem.itemName);
                                        }
                                    }
                                } else {
                                    const customItem = row.extraData?.["customItem"] as GroupedCustomDriver;
                                    if(customItem) {
                                        itemIds.push(customItem.driverId);
                                        drivers.push(customItem);
                                        itemNames.add(customItem.itemName);
                                    }
                                }

                                const allLocked = drivers.length > 0 && drivers.every(d => d.locked);
                                const allValuesLocked = drivers.length > 0 && drivers.every(d => d.valuesLocked);

                                setSetStatusModal({
                                    driverIds: itemIds,
                                    itemName: Array.from(itemNames),
                                    initialLocked: allLocked,
                                    initialValuesLocked: allValuesLocked
                                });
                            }
                        }
                    },
                    "viewNote": {
                        name: "View/Update Driver Note(s)",
                        hidden: (_selectedRows, selectedCols) => {
                            const isValidCol = Array.from(selectedCols.values()).max() <= 3;
                            return !(selectedCols.size === 1 && isValidCol);
                        },
                        disabled: () => false,
                        callback: (_selectedRows, _selectedCols, selectedRowData) => {
                            const selectedLineItemIds = new Set<string>();
                            const selectedLineItemNames = new Set<string>();

                            const filteredSelection = filterSelectedLineItems(selectedRowData);

                            for (const selectedItem of filteredSelection) {
                                const rowData = selectedItem.rowData;
                                if (rowData.children && rowData.children.length > 0) {
                                    for (const child of rowData.children) {
                                        const customItem = child.extraData?.["customItem"] as GroupedCustomDriver;
                                        if (customItem) {
                                            selectedLineItemIds.add(customItem.driverId);
                                            selectedLineItemNames.add(customItem.itemName);
                                        }
                                    }
                                } else {
                                    const customItem = rowData.extraData?.["customItem"] as GroupedCustomDriver;
                                    if (customItem) {
                                        selectedLineItemIds.add(customItem.driverId);
                                        selectedLineItemNames.add(customItem.itemName);
                                    }
                                }
                            }

                            const idArray = Array.from(selectedLineItemIds);
                            const nameArray = Array.from(selectedLineItemNames);
                            if (idArray.length === 1) {
                                const firstRowId = idArray[0];
                                const firstRowName = nameArray[0];

                                if (firstRowId) {
                                    setEditingNoteLineId(firstRowId);
                                    setEditingNoteLineName(firstRowName ?? "");
                                }
                            } else if (idArray.length > 1) {
                                setBulkEditingNoteLineIds(idArray);
                                setBulkEditingNoteLineNames(nameArray);
                            }
                        }
                    },
                }}
                hotProps={{
                    height: "50vh",
                    colHeaders: [getColumnLabel(0, groupBy), getColumnLabel(1, groupBy), getColumnLabel(2, groupBy), "Non-Admin Access", "Type", ...months],
                    colWidths: [150, 150, 150, 150, 120, ...(new Array(12).fill(90))],
                    fixedColumnsStart: 5,
                    autoRowSize: false,
                    autoColumnSize: false,
                    rowHeights: 40,
                    undo: false
                }}
                onChange={handleChange}
            />
            <SetStatusModal
                visible={setStatusModal !== null}
                budgetYear={props.budgetYear}
                itemName={setStatusModal?.itemName ?? []}
                itemIds={setStatusModal?.driverIds ?? []}
                itemLabel="Custom Driver"
                itemLabelPlural="Custom Drivers"
                initialLocked={setStatusModal?.initialLocked ?? false}
                initialValuesLocked={setStatusModal?.initialValuesLocked ?? false}
                submitting={setStatusLoading}
                onSubmit={async (permissionLevel) => {
                    const result = await setCustomDriversStatus({
                        variables: {
                            ids: setStatusModal?.driverIds ?? [],
                            budgetYear: props.budgetYear,
                            locked: permissionLevel === "editOnly",
                            valuesLocked: permissionLevel === "readOnly",
                        },
                    });
                    return !!result.data?.setCustomDriversStatus;
                }}
                onClose={() => setSetStatusModal(null)}
                onStatusSet={() => {
                    setSetStatusModal(null);
                    refetch();
                }}
            />
            <DeleteLineItemsModal
                visible={deleteModal !== null}
                itemIds={deleteModal?.driverIds ?? []}
                itemLabel="Custom Driver"
                itemLabelPlural="Custom Drivers"
                submitting={deleteCustomDriversLoading}
                onDelete={async () => {
                    const result = await deleteCustomDrivers({
                        variables: {
                            ids: deleteModal?.driverIds ?? [],
                            budgetYear: props.budgetYear,
                        },
                    });
                    return !!result.data?.deleteCustomDrivers;
                }}
                onClose={() => setDeleteModal(null)}
                onDeleted={() => {
                    const count = deleteModal?.driverIds.length ?? 0;
                    const label = count === 1 ? "custom driver" : "custom drivers";
                    toast.success(`Deleted ${count} ${label}`);
                    setDeleteModal(null);
                    refetch();
                }}
                onDeleteFailed={() => {
                    toast.error("Failed to delete custom drivers");
                }}
            />
            {copyModal && (
                <CopyCustomDriverAssumptionsModal
                    visible={copyModal !== null}
                    budgetYear={props.budgetYear}
                    versionType={props.versionType}
                    sourceDriver={copyModal.sourceDriver}
                    destinationProperties={copyModal.destinationProperties}
                    defaultSelectedProperties={copyModal.defaultSelectedProperties}
                    accounts={copyModal.accounts}
                    onClose={() => setCopyModal(null)}
                    onCopied={() => {
                        setCopyModal(null);
                        refetch();
                    }}
                />
            )}
            {addTooltipVisible && addButtonRef.current &&
                <CreateCustomDriverTooltipModal
                    budgetYear={props.budgetYear}
                    versionType={props.versionType}
                    properties={properties ?? []}
                    locksLookup={locksLookup}
                    referenceElement={addButtonRef.current}
                    onClose={() => setAddTooltipVisible(false)}
                    onCreated={() => {
                        setAddTooltipVisible(false);
                        refetch();
                    }}
                />
            }
            <CustomDriverNoteModal
                lineName={editingNoteLineName}
                lineId={editingNoteLineId}
                onSave={() => setEditingNoteLineId(null)}
                onClose={() => setEditingNoteLineId(null)}
            />

            <CustomBulkDriverNoteModal
                lineNames={bulkEditingNoteLineNames}
                lineIds={bulkEditingNoteLineIds}
                onSave={() => setBulkEditingNoteLineIds(null)}
                onClose={() => setBulkEditingNoteLineIds(null)}
            />
        </div>
    );
}

const MemoizedSimpleHoTWrapper = memo(SimpleHoTWrapper, (prevProps, nextProps) => {
    return prevProps.rows === nextProps.rows;
});
